import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { getTime } from 'date-fns';
import { useAuth } from 'hooks';
import { reportsTransactionsService } from 'services';
import useSWR, { useSWRConfig } from 'swr';

interface GenerateReportParams {
  startDate: Date;
  endDate: Date;
}

// Controle global do polling para manter ativo entre páginas
const POLLING_INTERVAL_MS = 3000;
let globalPollingInterval: NodeJS.Timeout | null = null;
let isPollingActive = false;
let lastStatus: string | null = null;

// Função para iniciar polling global
const startGlobalPolling = (
  store: string,
  reportKey: string,
  globalMutate: any
) => {
  if (isPollingActive) return;

  isPollingActive = true;
  globalPollingInterval = setInterval(async () => {
    try {
      const status = await reportsTransactionsService.getReportStatus(store);
      globalMutate(reportKey, status, false);

      // Verificar mudança de status para DONE e mostrar toast
      if (status?.status === 'DONE' && lastStatus !== 'DONE') {
        toast.success('Relatório financeiro pronto');
        stopGlobalPolling();
      }

      lastStatus = status?.status || null;
    } catch (error) {
      console.error('Polling error:', error);
      toast.error('Erro ao verificar status do relatório');
    }
  }, POLLING_INTERVAL_MS);
};

// Função para parar polling global
const stopGlobalPolling = () => {
  if (globalPollingInterval) {
    clearInterval(globalPollingInterval);
    globalPollingInterval = null;
  }
  isPollingActive = false;
  lastStatus = null;
};

export function useGenerateReport() {
  const { selectedStore } = useAuth();
  const [generateLoading, setGenerateLoading] = useState(false);
  const { mutate: globalMutate } = useSWRConfig();
  const store = selectedStore.name || '';
  const reportKey = `report-status-${store}`;

  const {
    data: reportStatus,
    mutate: mutateReportStatus,
    error: reportStatusError,
  } = useSWR(
    reportKey,
    () => reportsTransactionsService.getReportStatus(store),
    {
      revalidateOnFocus: false,
      refreshInterval: 0, // Desabilitado - usaremos controle manual
    }
  );

  useEffect(() => {
    if (reportStatus?.status === 'IN_PROGRESS' && !isPollingActive) {
      startGlobalPolling(store, reportKey, globalMutate);
    }
  }, [reportStatus?.status, store, reportKey, globalMutate]);

  async function generateReport({ startDate, endDate }: GenerateReportParams) {
    setGenerateLoading(true);
    try {
      await reportsTransactionsService.generateReport({
        startDate: getTime(startDate),
        endDate: getTime(endDate),
        storeName: store,
      });

      // Atualizar cache e iniciar polling
      mutateReportStatus();
      startGlobalPolling(store, reportKey, globalMutate);
      return true;
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Não foi possível gerar relatório');
      return false;
    } finally {
      setGenerateLoading(false);
    }
  }

  return {
    generateReport,
    generateLoading,
    reportStatus,
    reportStatusError,
  };
}
